import { CaseWorkerIdFilter_AllBeneficiaries } from '@repo/shared/dtos/caseWorker'
import {
  TakeSnapshotBeneficiaryOptions,
  TakeSnapshotOrganizationOptions,
} from '@repo/shared/dtos/snapshot'
import { Button } from '@repo/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/dialog'
import { useT } from '@repo/ui/hooks'
import { InlineCode } from '@repo/ui/inlineCode'
import { Label } from '@repo/ui/label'
import { Switch } from '@repo/ui/switch'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { isEqual } from 'lodash'
import { useState } from 'react'

import { ModalHandler } from '@/components/sudo/organization/OrganizationList'
import { sudoStoreActions } from '@/store/actions'
import { useStoreDispatch } from '@/store/hooks'

export function OrganizationSnapshoter({ modalHandler }: ModalHandler) {
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      modalHandler.setOrganization(null)
    }
  }
  return (
    <Dialog
      open={!!modalHandler.organization}
      defaultOpen={!!modalHandler.organization}
      onOpenChange={handleOpenChange}
    >
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{modalHandler.organization?.name}</DialogTitle>
          <DialogDescription>Configure snapshot's options.</DialogDescription>
        </DialogHeader>
        <OrganizationSnapshoterForm modalHandler={modalHandler} />
      </DialogContent>
    </Dialog>
  )
}

const defaultOrganizationOptions: TakeSnapshotOrganizationOptions = {
  withOrganizationData: true,
  withResources: true,
  withPromptTemplates: true,
  withReportTemplates: true,
}

const defaultBeneficiaryOptions: TakeSnapshotBeneficiaryOptions = {
  withTasks: true,
  withDocuments: true,
  withChatWithAlerts: true,
  withCaseworkers: true,
}

function OrganizationSnapshoterForm({ modalHandler }: ModalHandler) {
  const dispatch = useStoreDispatch()
  const { tc } = useT()

  const [organizationOptions, setOrganizationOptions] = useState<TakeSnapshotOrganizationOptions>({
    ...defaultOrganizationOptions,
  })

  const [beneficiaryOptions, setBeneficiaryOptions] = useState<TakeSnapshotBeneficiaryOptions>({
    ...defaultBeneficiaryOptions,
  })

  const handleTakeSnapshot = () => {
    if (!modalHandler.organization) return
    void dispatch(
      sudoStoreActions.snapshot.takeSnapshot({
        organizationId: modalHandler.organization.id,
        organizationOptions,
        beneficiaryOptions,
        onlyBeneficiaryIds: [CaseWorkerIdFilter_AllBeneficiaries],
      }),
    )
    modalHandler.setOrganization(null)
  }

  const handleOrganizationOptionsChange = (key: string, value: boolean) => {
    setOrganizationOptions((prev) => {
      // @ts-expect-error ignore
      prev[key] = value
      return { ...prev }
    })
  }

  const handleBeneficiaryOptionsChange = (key: string, value: boolean) => {
    setBeneficiaryOptions((prev) => {
      // @ts-expect-error ignore
      prev[key] = value
      return { ...prev }
    })
  }

  const handleReset = () => {
    setOrganizationOptions({ ...defaultOrganizationOptions })
    setBeneficiaryOptions({ ...defaultBeneficiaryOptions })
  }

  const disabled =
    isEqual(organizationOptions, defaultOrganizationOptions) &&
    isEqual(beneficiaryOptions, defaultBeneficiaryOptions)

  return (
    <div className="flex flex-col gap-8 mt-4">
      <List
        title="Organization Options:"
        items={organizationOptions}
        onClick={handleOrganizationOptionsChange}
      />

      <List
        title="Beneficiary Options:"
        items={beneficiaryOptions}
        onClick={handleBeneficiaryOptionsChange}
      />

      <div className="flex justify-between items-center">
        <TooltipWrapper content={tc('reset', { cfl: true })}>
          <Button onClick={handleReset} variant="outline" disabled={disabled}>
            {tc('reset', { cfl: true })}
          </Button>
        </TooltipWrapper>

        <Button onClick={handleTakeSnapshot}>{tc('confirm', { cfl: true })} </Button>
      </div>
    </div>
  )
}

function List({
  title,
  items,
  onClick,
}: {
  title: string
  items: Record<string, boolean>
  onClick: (key: string, value: boolean) => void
}) {
  const handleClick = (key: string) => (value: boolean) => {
    onClick(key, value)
  }
  return (
    <div className="flex flex-col gap-4">
      <InlineCode className="px-4 font-medium">{title}</InlineCode>
      <div className="flex flex-col gap-2">
        {Object.keys(items).map((key) => {
          const value = items[key]
          return (
            <div
              key={key}
              className="flex items-center justify-between w-full border border-primary-100 cursor-pointer hover:bg-primary-50 px-4 py-2 rounded-lg"
              onClick={() => handleClick(key)(!value)}
            >
              <Label>{key}</Label>
              <Switch checked={value} onCheckedChange={handleClick(key)} />
            </div>
          )
        })}
      </div>
    </div>
  )
}
